import { Route } from '@angular/router';
import { initialDataResolver } from 'app/app.resolvers';
import { AuthGuard } from 'app/core/auth/guards/auth.guard';
import { NoAuthGuard } from 'app/core/auth/guards/noAuth.guard';
import { LayoutComponent } from 'app/layout/layout.component';
import { ExtrasStateResolver } from './api/shared/extrasStateResolver';
import { pendingChangesGuard } from './shared/guards/pending-changes.guard';

export const appRoutes: Route[] = [
    { path: '', pathMatch: 'full', redirectTo: '/admin/home' },
    { path: 'signed-in-redirect', pathMatch: 'full', redirectTo: '' },
    { path: 'crews', pathMatch: 'full', redirectTo: 'crews' },
    { path: 'kbs', pathMatch: 'full', redirectTo: 'kbs' },
    { path: 'supervisor', pathMatch: 'full', redirectTo: 'supervisor' },
    { path: 'history', pathMatch: 'full', redirectTo: 'history' },
    { path: 'reports', pathMatch: 'full', redirectTo: 'reports' },
    { path: 'billing', pathMatch: 'full', redirectTo: 'billing' },
    { path: 'credentials', pathMatch: 'full', redirectTo: 'credentials' },
    { path: 'custom-reports', pathMatch: 'full', redirectTo: 'custom-reports' },
    { path: 'dragula', pathMatch: 'full', redirectTo: 'dragula' },
    { path: 'credentials', pathMatch: 'full', redirectTo: 'credentials' },
    {
        path: 'recycle-bin',
        pathMatch: 'full',
        redirectTo: 'recycle-bin',
    },

    // // Auth routes for non-authenticated users
    {
        path: '',
        canActivate: [NoAuthGuard],
        canActivateChild: [NoAuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: 'dragula',
                loadChildren: () =>
                    import('app/shared/components/others/dragula/drag-and-drop.module').then((m) => m.DragAndDropModule),
                data: { isLegacy: true },
            },
            {
                path: 'recycle-bin',
                loadChildren: () =>
                    import(
                        'app/modules/admin/recycle-bin/recycle-bin.routes'
                    ),
            },
            {
                path: 'crews',
                loadChildren: () =>
                    import('app/modules/admin/crews/routes/crew.routes'),
                data: { isLegacy: true },
            },
            {
                path: 'kbs',
                loadChildren: () =>
                    import('app/modules/admin/kbs/kbs.routes'),
            },
            {
                path: 'supervisor',
                loadChildren: () =>
                    import(
                        'app/modules/admin/supervisor/supervisor.routes'
                    ),
            },
            {
                path: 'reports',
                loadChildren: () =>
                    import(
                        'app/modules/admin/reports/routes/reports.routes'
                    ),
                data: { isLegacy: true },
            },
            {
                path: 'custom-reports',
                loadChildren: () =>
                    import(
                        'app/modules/admin/custom-reports/routes/custom-reports.routes'
                    ),
                resolve: {
                    extrasState: ExtrasStateResolver
                },
                data: { isLegacy: true },
            },
            {
                path: 'history',
                loadChildren: () =>
                    import(
                        'app/modules/admin/history/history.routes'
                    ),
            },
            {
                path: 'auto-contact',
                loadChildren: () =>
                    import(
                        'app/modules/admin/auto-contact/auto-contact.routes'
                    ),
            },
            {
                path: 'confirmation-required',
                loadChildren: () =>
                    import(
                        'app/modules/auth/confirmation-required/confirmation-required.routes'
                    ),
            },
            {
                path: 'forgot-password',
                loadChildren: () =>
                    import(
                        'app/modules/auth/forgot-password/forgot-password.routes'
                    ),
            },
            {
                path: 'reset-password',
                loadChildren: () =>
                    import(
                        'app/modules/auth/reset-password/reset-password.routes'
                    ),
            },
            {
                path: 'sign-in',
                loadChildren: () =>
                    import('app/modules/auth/sign-in/sign-in.routes'),
            },
            {
                path: 'sign-up',
                loadChildren: () =>
                    import('app/modules/auth/sign-up/sign-up.routes'),
            },
            {
                path: 'billing',
                loadChildren: () =>
                    import('app/modules/admin/billing/routes/billing.routes'),
                data: { isLegacy: true },
            },
            {
                path: 'credentials',
                loadChildren: () =>
                    import('app/modules/admin/credentials/credentials.routes'),
            },
            {
                path: 'configuration',
                loadChildren: () =>
                    import('app/modules/admin/configuration/configuration.routes'),
            },
            {
                path: 'users',
                redirectTo: 'configuration/user-management/users',
                pathMatch: 'prefix',
            }
        ],
    },

    // // Auth routes for authenticated users
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: 'dragula',
                loadChildren: () =>
                    import('app/shared/components/others/dragula/drag-and-drop.module').then((m) => m.DragAndDropModule),
                data: { isLegacy: true },
            },
            {
                path: 'recycle-bin',
                loadChildren: () =>
                    import(
                        'app/modules/admin/recycle-bin/recycle-bin.routes'
                    ),
            },
            {
                path: 'crews',
                loadChildren: () =>
                    import('app/modules/admin/crews/routes/crew.routes'),
                data: { isLegacy: true },
            },
            {
                path: 'kbs',
                loadChildren: () =>
                    import('app/modules/admin/kbs/kbs.routes'),
            },
            {
                path: 'supervisor',
                loadChildren: () =>
                    import(
                        'app/modules/admin/supervisor/supervisor.routes'
                    ),
            },
            {
                path: 'reports',
                loadChildren: () =>
                    import(
                        'app/modules/admin/reports/routes/reports.routes'
                    ),
                data: { isLegacy: true },
            },
            {
                path: 'custom-reports',
                loadChildren: () =>
                    import(
                        'app/modules/admin/custom-reports/routes/custom-reports.routes'
                    ),
                resolve: {
                    extrasState: ExtrasStateResolver
                },
                data: { isLegacy: true },
            },
            {
                path: 'history',
                loadChildren: () =>
                    import(
                        'app/modules/admin/history/history.routes'
                    ),
            },
            {
                path: 'auto-contact',
                loadChildren: () =>
                    import(
                        'app/modules/admin/auto-contact/auto-contact.routes'
                    ),
            },

            {
                path: 'sign-out',
                loadChildren: () =>
                    import('app/modules/auth/sign-out/sign-out.routes'),
            },
            {
                path: 'unlock-session',
                loadChildren: () =>
                    import(
                        'app/modules/auth/unlock-session/unlock-session.routes'
                    ),
            },
            {
                path: 'users',
                redirectTo: 'configuration/user-management/users',
                pathMatch: 'prefix',
            },
            {
                path: 'billing',
                loadChildren: () =>
                    import('app/modules/admin/billing/routes/billing.routes'),
                data: { isLegacy: true },
            },
            {
                path: 'credentials',
                loadChildren: () =>
                    import('app/modules/admin/credentials/credentials.routes'),
            },
            {
                path: 'configuration',
                loadChildren: () =>
                    import('app/modules/admin/configuration/configuration.routes'),
            },
        ],
    },

    // Landing routes
    {
        path: '',
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: 'dragula',
                loadChildren: () =>
                    import('app/shared/components/others/dragula/drag-and-drop.module').then((m) => m.DragAndDropModule),
                data: { isLegacy: true },
            },
            {
                path: 'recycle-bin',
                loadChildren: () =>
                    import(
                        'app/modules/admin/recycle-bin/recycle-bin.routes'
                    ),
            },
            {
                path: 'crews',
                loadChildren: () =>
                    import('app/modules/admin/crews/routes/crew.routes'),
                data: { isLegacy: true },
            },
            {
                path: 'kbs',
                loadChildren: () =>
                    import('app/modules/admin/kbs/kbs.routes'),
            },
            {
                path: 'supervisor',
                loadChildren: () =>
                    import(
                        'app/modules/admin/supervisor/supervisor.routes'
                    ),
            },
            {
                path: 'reports',
                loadChildren: () =>
                    import(
                        'app/modules/admin/reports/routes/reports.routes'
                    ),
                data: { isLegacy: true },
            },
            {
                path: 'custom-reports',
                loadChildren: () =>
                    import(
                        'app/modules/admin/custom-reports/routes/custom-reports.routes'
                    ),
                resolve: {
                    extrasState: ExtrasStateResolver
                },
                data: { isLegacy: true },
            },
            {
                path: 'history',
                loadChildren: () =>
                    import(
                        'app/modules/admin/history/history.routes'
                    ),
            },
            {
                path: 'auto-contact',
                loadChildren: () =>
                    import(
                        'app/modules/admin/auto-contact/auto-contact.routes'
                    ),
            },

            {
                path: 'home',
                loadChildren: () =>
                    import('app/modules/admin/home/home.routes'),
            },
            {
                path: 'billing',
                loadChildren: () =>
                    import('app/modules/admin/billing/routes/billing.routes'),
                data: { isLegacy: true },
            },
            {
                path: 'credentials',
                loadChildren: () =>
                    import('app/modules/admin/credentials/credentials.routes'),
            },
            {
                path: 'configuration',
                loadChildren: () =>
                    import('app/modules/admin/configuration/configuration.routes'),
            }
        ],
    },

    // Admin routes
    {
        path: 'admin',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver,
        },
        children: [
            {
                path: 'dragula',
                loadChildren: () =>
                    import('app/shared/components/others/dragula/drag-and-drop.module').then((m) => m.DragAndDropModule),
                data: { isLegacy: true },
            },
            {
                path: 'recycle-bin',
                loadChildren: () =>
                    import(
                        'app/modules/admin/recycle-bin/recycle-bin.routes'
                    ),
            },
            {
                path: 'crews',
                loadChildren: () =>
                    import('app/modules/admin/crews/routes/crew.routes'),
                data: { isLegacy: true },
            },
            {
                path: 'kbs',
                loadChildren: () =>
                    import('app/modules/admin/kbs/kbs.routes'),
            },
            {
                path: 'supervisor',
                loadChildren: () =>
                    import(
                        'app/modules/admin/supervisor/supervisor.routes'
                    ),
            },
            {
                path: 'reports',
                loadChildren: () =>
                    import(
                        'app/modules/admin/reports/routes/reports.routes'
                    ),
                data: { isLegacy: true },
            },
            {
                path: 'custom-reports',
                loadChildren: () =>
                    import(
                        'app/modules/admin/custom-reports/routes/custom-reports.routes'
                    ),
                resolve: {
                    extrasState: ExtrasStateResolver
                },
                data: { isLegacy: true },
            },
            {
                path: 'channels',
                loadChildren: () =>
                    import(
                        'app/modules/admin/channels/routes/channels.routes'
                    ),
                resolve: {},
                data: { isLegacy: true },
            },
            {
                path: 'credentials',
                loadChildren: () =>
                    import('app/modules/admin/credentials/credentials.routes'),
            },
            {
                path: 'history',
                loadChildren: () =>
                    import(
                        'app/modules/admin/history/history.routes'
                    ),
            },
            {
                path: 'guards',
                loadChildren: () =>
                    import('app/modules/admin/guards/routes/guard.routes'),
                data: { isLegacy: true },
            },
            {
                path: 'modifiers',
                loadChildren: () =>
                    import(
                        'app/modules/admin/modifiers/routes/modifier.routes'
                    ),
                data: { isLegacy: true },
            },
            {
                path: 'home',
                loadChildren: () =>
                    import('app/modules/admin/home/home.routes'),
            },
            {
                path: 'example',
                loadChildren: () =>
                    import('app/modules/admin/example/example.routes'),
                data: { isLegacy: true },
            },
            {
                path: 'auto-contact',
                loadChildren: () =>
                    import('app/modules/admin/auto-contact/auto-contact.routes'),
            },
            {
                path: 'examples',
                loadChildren: () =>
                    import('app/modules/admin/examples/examples.routes'),
            },
            {
                path: 'crew-speaking/:id',
                loadChildren: () =>
                    import('app/modules/admin/crews/components/crew-detail-styles-speaking-config/crew-detail-styles-speaking-config.routes'),
                resolve: {
                    extrasState: ExtrasStateResolver
                },
                data: { isLegacy: true },
            },

            {
                path: 'crew-modifiers/:id',
                loadChildren: () =>
                    import('app/modules/admin/crews/components/crew-detail-styles-modifiers-config/crew-detail-styles-modifiers-config.routes'),
                resolve: {
                    extrasState: ExtrasStateResolver
                },
                data: { isLegacy: true },
            },
            {
                path: 'crew-session/:id',
                loadChildren: () =>
                    import('app/modules/admin/crews/components/crew-detail-styles-session/crew-detail-styles-session.routes'),
                resolve: {
                    extrasState: ExtrasStateResolver
                },
                data: { isLegacy: true },
            },

            {
                path: 'crews',
                canActivate: [NoAuthGuard],
                canActivateChild: [NoAuthGuard],
                component: LayoutComponent,
                data: {
                    layout: 'empty',
                },
                children: [
                    {
                        path: 'list',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/crews/routes/crew.routes'
                            ),
                        data: { isLegacy: true },
                    },
                ],
            },
            {
                path: 'users',
                redirectTo: 'configuration/user-management/users',
                pathMatch: 'prefix',
            },
            {
                path: 'billing',
                loadChildren: () =>
                    import('app/modules/admin/billing/routes/billing.routes'),
                data: { isLegacy: true },
            },
            {
                path: 'configuration',
                loadChildren: () =>
                    import('app/modules/admin/configuration/configuration.routes'),
            },
            {
                // Página de la cuenta del usuario autenticado, accesible desde
                // el menú de perfil de la barra superior. Vive fuera de
                // `configuration` porque ya no es una pestaña de ese módulo.
                path: 'personal-info',
                loadChildren: () =>
                    import('app/modules/admin/personal-info/personal-info.routes'),
            },
            {
                path: 'builder',
                loadChildren: () =>
                    import('app/modules/admin/builder/builder.routes'),
                data: { isLegacy: true },
            },
            {
                path: 'evaluations',
                loadChildren: () =>
                    import('app/modules/admin/evaluations/evaluations.routes'),
            },
            // {
            //     path: 'dynamic-tool',
            //     loadChildren: () =>
            //         import('app/modules/admin/dynamic-tool/dynamic-tool.routes'),
            // }

            // Wildcard: redirige rutas no encontradas bajo /admin al home
            {
                path: 'credentials',
                loadChildren: () =>
                    import('app/modules/admin/credentials/credentials.routes'),
            },
            {
                path: '**',
                redirectTo: 'home',
            }
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: 'sign-out',
                loadChildren: () =>
                    import('app/modules/auth/sign-out/sign-out.routes'),
            },
        ]
    }
];
